import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import {
  isIaChatRole,
  isTeamRosterReadRole,
  type RecipeMeResponse,
  type Role,
} from "@bitacorapro/api-types";
import { Shell } from "./components/Shell";
import { AdminCargaMasiva } from "./features/admin/AdminCargaMasiva";
import { AdminOrgDetail } from "./features/admin/AdminOrgDetail";
import { AdminOrgReports } from "./features/admin/AdminOrgReports";
import { AdminOrgs } from "./features/admin/AdminOrgs";
import { AdminOverview } from "./features/admin/AdminOverview";
import { AdminShell } from "./features/admin/AdminShell";
import { RecipeGate } from "./components/RecipeGate";
import { useRecipe } from "./lib/api/queries";
import {
  canAdministerOrg,
  canImportUnits,
  canManageMaintenanceItems,
  canReadProviderRoster,
  canUseChecklistsSection,
  hasAdminModule,
  hasDashboardSurface,
  isFacilityDashboardEnabled,
  type OrgAdminOpts,
} from "./lib/capabilities";
import type { AdminSection } from "@bitacorapro/core-engine";
import {
  ADMIN_BASE_PATH,
  ADMIN_NAV_KEY,
  adminSectionPath,
  declaredAdminSections,
  hasContextualProjectNav,
  navItemBasePath,
  useVisibleNavItems,
} from "./lib/nav";
import { RequireAuth } from "./lib/auth/RequireAuth";
import { RequirePlatformAdmin } from "./lib/auth/RequirePlatformAdmin";
import { RequireDashboardRole } from "./lib/auth/RequireDashboardRole";
import { SessionExpiryHandler } from "./lib/auth/SessionExpiryHandler";
import { useSession } from "./lib/auth/useSession";
import { useAppReadySignal } from "./lib/snapshot/useAppReady";
import { usePostHogPageview } from "./lib/telemetry/usePostHogPageview";
import { AuthCallback } from "./routes/AuthCallback";
import { CargarUnidades } from "./routes/CargarUnidades";
import { DocumentosProyecto } from "./routes/DocumentosProyecto";
import { Configuracion } from "./routes/Configuracion";
import { ConsultasDelProyectoRoute } from "./routes/ConsultasDelProyectoRoute";
import { AvisosDelProyectoRoute } from "./routes/AvisosDelProyectoRoute";
import { EncuestasDelProyectoRoute } from "./routes/EncuestasDelProyectoRoute";
import { ContextoFuenteDetalle } from "./routes/ContextoFuenteDetalle";
import { ContextoMarca } from "./routes/ContextoMarca";
import { ContextoRevision } from "./routes/ContextoRevision";
import { ContextoVersionDiff } from "./routes/ContextoVersionDiff";
import { ArchivoProyecto } from "./routes/ArchivoProyecto";
import { DetalleProyecto } from "./routes/DetalleProyecto";
import { DetalleTienda } from "./routes/DetalleTienda";
import { Contratistas } from "./routes/Contratistas";
import { EnlacesCompartidos } from "./routes/EnlacesCompartidos";
import { Equipo } from "./routes/Equipo";
import { AnalisisRoute } from "./routes/AnalisisRoute";
import { IAInsights } from "./routes/IAInsights";
import { Login } from "./routes/Login";
import { DetalleObservacionPostventa } from "./routes/DetalleObservacionPostventa";
import { ObservacionDetailRoute } from "./routes/ObservacionDetailRoute";
import { ObservacionesDelProyectoRoute } from "./routes/ObservacionesDelProyectoRoute";
import { ObservacionesRoute } from "./routes/ObservacionesRoute";
import { OverviewRoute } from "./routes/OverviewRoute";
import { Proyectos } from "./routes/Proyectos";
import { Reporte } from "./routes/Reporte";
import { Tiendas } from "./routes/Tiendas";
import Activos from "./routes/administracion/Activos";
import ChecklistsAdmin from "./routes/administracion/Checklists";
import ContratistasAdmin from "./routes/administracion/Contratistas";
import Empresa from "./routes/administracion/Empresa";
import Enlaces from "./routes/administracion/Enlaces";
import EquipoAdmin from "./routes/administracion/Equipo";
import Mantenciones from "./routes/administracion/Mantenciones";
import Registro from "./routes/administracion/Registro";
import Resumen from "./routes/administracion/Resumen";
import { DEV_ROUTES_ENABLED } from "./lib/env/devRoutes";
// Placeholder.tsx is no longer imported but kept for the remaining
// stub screens (#17, #18) until they ship.

// Module scope, not component scope — lazy() must be called once. On a
// build where DEV_ROUTES_ENABLED folds to a literal `false` (see
// lib/env/devRoutes.ts), this whole branch and the dynamic import() are
// dead code Rollup drops, so /dev/** never reaches the emitted chunk graph
// (BIT-74, ADR-0076; the exclusion mechanism ADR-0076's Consequences says
// has to be built alongside this route, not inherited from the PWA's
// directory-move-based BIT-60 fix, which doesn't apply to this app's
// centralized react-router-dom tree).
const DevRoutes = DEV_ROUTES_ENABLED ? lazy(() => import("./routes/dev/DevRoutes")) : null;

// Route-level defense-in-depth for /ia (ADR-0139): the Sidebar nav entry
// already hides this route for roles outside IA_CHAT_ROLE_KEYS (the chat
// is role-scoped server-side, so facility_supervisor now reaches it with
// its own stores' facts), but a direct URL should degrade to a redirect,
// not the 403 the underlying POST /ia/chat call would otherwise surface
// as a broken screen.
function RequireIaChat({ children }: { children: ReactNode }) {
  const session = useSession();
  if (!isIaChatRole(session?.organization.role ?? "")) {
    return <Navigate to="/" replace />;
  }
  // POST /ia/chat answers VERTICAL_REQUIRED outside facility: a stale
  // bookmark from a postventa role lands on its home, not a dead chat.
  return (
    <RecipeGate allow={isFacilityDashboardEnabled} redirectTo="/">
      {children}
    </RecipeGate>
  );
}

// Route-level defense-in-depth for /equipo (product decision 2026-09-03,
// amends ADR-0089). The Sidebar nav entry already hides this route for
// roles outside TEAM_ROSTER_READ_ROLE_KEYS, but a stale bookmark should
// land somewhere useful instead of rendering the org's full people
// directory. Redirects to "/", not a terminal no-access screen: a
// store_manager still has a working dashboard, only this one surface is
// out of scope for them.
function RequireTeamRosterRead({ children }: { children: ReactNode }) {
  const session = useSession();
  if (!isTeamRosterReadRole(session?.organization.role ?? "")) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Route-level defense-in-depth for /contratistas. The nav entry is
// already gated on the recipe having an observation lifecycle
// (ADR-0058: a non-empty observationStatuses vocabulary — never
// recipe.slug, §0.4(6)), because the roster only exists to feed the
// assignment drawer; an org without that lifecycle reaching it by URL or a
// stale ⌘K entry would land on a screen it has no use for (since ADR-0134
// facility has the lifecycle and its «Contratistas» roster). Same posture as
// OverviewRoute: an unresolved recipe draws a skeleton rather than a
// redirect, so a slow /recipes/me never bounces a postventa user off a
// screen they are entitled to (RecipeGate).
export function RequireObservationLifecycle({ children }: { children: ReactNode }) {
  return (
    <RecipeGate allow={(recipe) => recipe.observationStatuses.length > 0} redirectTo="/">
      {children}
    </RecipeGate>
  );
}

// The project-scoped observations list is postventa's working surface
// (panel postventa v2): it reads the postventa feed. Since ADR-0134
// facility ALSO has an observation lifecycle, so RequireObservationLifecycle
// no longer keeps it out — a facility zona is not a project with a list.
// Gated on the same predicate the sidebar uses for the project block
// (`hasContextualProjectNav`); anything else lands on the org-wide list.
export function RequireContextualProjectNav({ children }: { children: ReactNode }) {
  return (
    <RecipeGate allow={hasContextualProjectNav} redirectTo="/observaciones">
      {children}
    </RecipeGate>
  );
}

// Route-level defense-in-depth for /carga-unidades (ADR-0144 §2): the nav
// entry is already gated on canImportUnits; a direct URL from a role or a
// vertical without the importer lands on "/" instead of a screen whose
// every call would 403. An unresolved recipe draws a skeleton, same as
// RequireObservationLifecycle (RecipeGate).
export function RequireUnitImport({ children }: { children: ReactNode }) {
  const session = useSession();
  return (
    <RecipeGate
      allow={(recipe) =>
        canImportUnits(recipe, session?.organization.role, session?.user.platformAdmin)
      }
      redirectTo="/"
    >
      {children}
    </RecipeGate>
  );
}

// Route-level defense-in-depth for every shell screen except the index
// and catch-all: an org whose recipe resolves to no dashboard surface
// (resolveHomeSurface === "none" — residential_rental today) never
// reaches Tiendas / Observaciones / Equipo… by URL. The Shell already
// swaps its body for NoDashboardSurface in that state; this guard keeps
// the route tree honest on its own. Loading draws RecipeGate's neutral
// skeleton, never a redirect, so facility and postventa deep links are
// untouched. Never recipe.slug (ADR-0072).
export function RequireDashboardSurface({ children }: { children: ReactNode }) {
  return (
    <RecipeGate allow={hasDashboardSurface} redirectTo="/">
      {children}
    </RecipeGate>
  );
}

// Autogestión (plan 2026-10-30 §1.1): una sección nueva de Administración
// (Tiendas y zonas, Mantenciones) es de quien administra la organización
// según la receta —canAdministerOrg y los gates que derivan de él, el
// espejo de requireOrgAdmin en core-api—, nunca según el slug. El menú ya
// la esconde a los demás (requiresOrgAdmin en nav.ts); por URL un jefe
// de tienda vuelve a "/", que sigue siendo su dashboard, igual que
// RequireTeamRosterRead. Receta cargando: el esqueleto de RecipeGate,
// nunca un redirect. Recibe el predicado para que la ruta nombre el mismo
// gate que la pantalla lee (canManageMaintenanceItems en Mantenciones).
export function RequireRoleCapability({
  allow,
  children,
}: {
  allow: (recipe: RecipeMeResponse, role: Role | undefined, opts: OrgAdminOpts) => boolean;
  children: ReactNode;
}) {
  const session = useSession();
  const role = session?.organization.role;
  const platformAdmin = session?.user.platformAdmin === true;
  return (
    <RecipeGate allow={(recipe) => allow(recipe, role, { platformAdmin })} redirectTo="/">
      {children}
    </RecipeGate>
  );
}

// Si la receta declara la sección y la sección tiene pantalla.
function declaresAdminSection(
  recipe: Parameters<typeof declaredAdminSections>[0],
  section: AdminSection
): boolean {
  return declaredAdminSections(recipe).some((s) => s.section === section);
}

// Autogestión (plan 2026-10-30 §1.5): las rutas /administracion/* sólo
// existen para una receta que declara `adminModule` (y, en una sección,
// esa sección). Cualquier otra vuelve a la ruta de siempre de esa
// pantalla (o a "/"), así un enlace pegado desde facility no deja a nadie
// en blanco. Cargando: el esqueleto de RecipeGate, nunca un redirect.
export function RequireAdminModule({
  section,
  fallback,
  children,
}: {
  section?: AdminSection;
  fallback: string;
  children: ReactNode;
}) {
  return (
    <RecipeGate
      allow={(recipe) =>
        section === undefined ? hasAdminModule(recipe) : declaresAdminSection(recipe, section)
      }
      redirectTo={fallback}
    >
      {children}
    </RecipeGate>
  );
}

// Una ruta suelta que en una receta con `adminModule` pasó a ser sección
// de Administración (/equipo, /contratistas, /enlaces): ahí redirige a la
// sección, conservando query y hash. Para las demás recetas —y para una
// que no declare esa sección— renderiza la pantalla exactamente como
// antes. Vive debajo de RequireDashboardSurface, que ya esperó a la
// receta, así que no hay un primer render con la pantalla vieja.
export function AdminModuleRedirect({
  section,
  children,
}: {
  section: AdminSection;
  children: ReactNode;
}) {
  const { data: recipe } = useRecipe();
  const { search, hash } = useLocation();
  if (declaresAdminSection(recipe, section)) {
    return <Navigate to={{ pathname: adminSectionPath(section), search, hash }} replace />;
  }
  return <>{children}</>;
}

// /administracion sin Resumen para este rol: el índice redirige a la
// primera sección que ve en el menú, o a "/" si no ve ninguna (el padre
// tampoco se muestra en ese caso). Una hija con la ruta del índice nunca
// es destino, para no redirigir en círculo.
export function AdminIndexRedirect() {
  const items = useVisibleNavItems();
  const first = items.find(
    (n) => n.parentKey === ADMIN_NAV_KEY && navItemBasePath(n) !== ADMIN_BASE_PATH
  );
  return <Navigate to={first?.path ?? "/"} replace />;
}

// /administracion (plan §2.1): la portada del módulo es la sección
// Resumen, para quien administra la organización (el mismo gate que la
// pantalla asume y que core-api repite en GET /organizations/me/
// setup-status). Un rol que ve el módulo sin administrarlo —el
// supervisor con Contratistas, el jefe de tienda con Enlaces— cae en la
// primera sección que sí ve (AdminIndexRedirect), igual que si la receta
// no declarara Resumen. Vive debajo de RequireAdminModule, que ya esperó
// a la receta, así que no hay un primer render con el redirect.
export function AdminIndex() {
  const { data: recipe } = useRecipe();
  const session = useSession();
  const role = session?.organization.role;
  const platformAdmin = session?.user.platformAdmin === true;
  if (
    declaresAdminSection(recipe, "resumen") &&
    canAdministerOrg(recipe, role, { platformAdmin })
  ) {
    return <Resumen />;
  }
  return <AdminIndexRedirect />;
}

// Las pantallas del shell que exigen una superficie de dashboard. Sale de
// App() como función —no componente: <Routes> sólo acepta <Route> como
// hijos— para que App.test.tsx monte el árbol real de rutas (guardas,
// redirects de Administración y su anidación bajo RequireDashboardSurface)
// en vez de una copia a mano que podría divergir.
export function dashboardSurfaceRoutes() {
  return (
    <Route
      element={
        <RequireDashboardSurface>
          <Outlet />
        </RequireDashboardSurface>
      }
    >
      <Route path="tiendas" element={<Tiendas />} />
      <Route path="tiendas/:id" element={<DetalleTienda />} />
      <Route path="proyectos" element={<Proyectos />} />
      <Route path="proyectos/:id" element={<DetalleProyecto />} />
      <Route path="proyectos/:id/archivo" element={<ArchivoProyecto />} />
      {/* ADR-0154 — análisis de cartera con IA. La cartera vive fuera
          de /proyectos/* para que el rail no lea «analisis» como id de
          proyecto; la guardia de rol y vertical está en la ruta. */}
      <Route path="analisis" element={<AnalisisRoute />} />
      <Route path="proyectos/:id/analisis" element={<AnalisisRoute />} />
      {/* Panel postventa v2 (design/claude-design/panel-postventa-v2
          §Navegación): the project's observations and their detail
          live under the project. Gated on the observation lifecycle
          like /contratistas — the list reads the postventa feed, which
          a facility org has no use for. The legacy /observaciones
          routes below keep serving facility and forward postventa
          here (ObservacionesRoute / ObservacionDetailRoute). */}
      <Route
        path="proyectos/:id/observaciones"
        element={
          <RequireContextualProjectNav>
            <ObservacionesDelProyectoRoute />
          </RequireContextualProjectNav>
        }
      />
      <Route
        path="proyectos/:id/observaciones/:obsId"
        element={
          <RequireObservationLifecycle>
            <DetalleObservacionPostventa />
          </RequireObservationLifecycle>
        }
      />
      {/* ADR-0145 — la bandeja de consultas del propietario vive bajo
          el proyecto, como las observaciones; `?consulta=<id>` abre el
          hilo (deep link del correo al jefe). Mismo gate que la lista:
          el proyecto como contexto de navegación. Quién puede verla lo
          decide el servidor (403/404). */}
      <Route
        path="proyectos/:id/consultas"
        element={
          <RequireContextualProjectNav>
            <ConsultasDelProyectoRoute />
          </RequireContextualProjectNav>
        }
      />
      {/* ADR-0152 — avisos y guías de mantención del proyecto, hermana
          de consultas. Quién publica lo decide el servidor (403/404). */}
      <Route
        path="proyectos/:id/avisos"
        element={
          <RequireContextualProjectNav>
            <AvisosDelProyectoRoute />
          </RequireContextualProjectNav>
        }
      />
      {/* ADR-0153 — encuestas del propietario del proyecto. Quién la
          ve lo decide el servidor (403/404). */}
      <Route
        path="proyectos/:id/encuestas"
        element={
          <RequireContextualProjectNav>
            <EncuestasDelProyectoRoute />
          </RequireContextualProjectNav>
        }
      />
      <Route path="observaciones" element={<ObservacionesRoute />} />
      <Route path="observaciones/:id" element={<ObservacionDetailRoute />} />
      <Route
        path="contratistas"
        element={
          <AdminModuleRedirect section="contratistas">
            <RequireObservationLifecycle>
              <Contratistas />
            </RequireObservationLifecycle>
          </AdminModuleRedirect>
        }
      />
      <Route
        path="enlaces"
        element={
          <AdminModuleRedirect section="enlaces">
            <EnlacesCompartidos />
          </AdminModuleRedirect>
        }
      />
      <Route
        path="ia"
        element={
          <RequireIaChat>
            <IAInsights />
          </RequireIaChat>
        }
      />
      <Route
        path="equipo"
        element={
          <AdminModuleRedirect section="equipo">
            <RequireTeamRosterRead>
              <Equipo />
            </RequireTeamRosterRead>
          </AdminModuleRedirect>
        }
      />
      <Route
        path="carga-unidades"
        element={
          <RequireUnitImport>
            <CargarUnidades />
          </RequireUnitImport>
        }
      />
      {/* ADR-0144 §4 — documentos del proyecto: mismo gating que el
          importador de unidades. */}
      <Route
        path="documentos"
        element={
          <RequireUnitImport>
            <DocumentosProyecto />
          </RequireUnitImport>
        }
      />
      {/* ADR-0173 fase 2 — the client's documents and context versions.
          Gated inside the screen on the org's context_ingest flag. */}
      <Route path="contexto" element={<ContextoMarca />} />
      <Route path="contexto/fuentes/:id" element={<ContextoFuenteDetalle />} />
      <Route path="contexto/fuentes/:id/reglas" element={<ContextoRevision />} />
      <Route path="contexto/versiones/:id/diff" element={<ContextoVersionDiff />} />
      {/* Con adminModule, Configuración vive en la portada de Administración
          (plan §1.5); sin él sigue la pantalla suelta de postventa/arriendos. */}
      <Route
        path="configuracion"
        element={
          <AdminModuleRedirect section="resumen">
            <Configuracion />
          </AdminModuleRedirect>
        }
      />
      {/* Autogestión (plan 2026-10-30 §1.5): el módulo Administración,
          sólo para una receta con `adminModule`. Una sección que absorbe
          una pantalla suelta la monta con sus mismas guardas; una
          sección nueva (Resumen, Empresa, Tiendas y zonas, Mantenciones,
          Registro de cambios) lleva el gate de administrador; las que aún
          no tienen pantalla no tienen ruta. Enlaces y Contratistas montan
          en una receta con el módulo la pantalla nueva de facility
          (routes/administracion/*): Enlaces esconde crear/revocar/registro
          a quien no administra y deja la lista —el GET es de cualquier
          rol—; Contratistas es de quien lee la nómina (el carril de
          asignación, espejo de GET /providers: gerente y supervisor) y
          esconde editar/desactivar a quien no administra. Equipo monta
          la pantalla nueva (routes/administracion/Equipo: roster,
          invitaciones pendientes y alcances por tienda) con la misma
          guarda de lectura que la ruta suelta —RequireTeamRosterRead— y
          gatea desactivar, invitar y editar acceso por capacidad dentro
          de la pantalla. Checklists (T-ljhq, lectura + asignar) lleva el
          gate de administrador más la capacidad de la receta de configurar
          checklists por plantilla (canUseChecklistsSection: GET
          /checklist-templates responde 403 a los demás); una receta con el
          módulo pero sin esa sección vuelve a la portada del módulo. Sin
          el módulo, las rutas sueltas siguen con Equipo,
          EnlacesCompartidos y Contratistas (postventa) tal cual. */}
      <Route path="administracion">
        <Route
          index
          element={
            <RequireAdminModule fallback="/">
              <AdminIndex />
            </RequireAdminModule>
          }
        />
        <Route
          path="empresa"
          element={
            <RequireAdminModule section="empresa" fallback="/">
              <RequireRoleCapability allow={canAdministerOrg}>
                <Empresa />
              </RequireRoleCapability>
            </RequireAdminModule>
          }
        />
        <Route
          path="equipo"
          element={
            <RequireAdminModule section="equipo" fallback="/equipo">
              <RequireTeamRosterRead>
                <EquipoAdmin />
              </RequireTeamRosterRead>
            </RequireAdminModule>
          }
        />
        <Route
          path="contratistas"
          element={
            <RequireAdminModule section="contratistas" fallback="/contratistas">
              <RequireRoleCapability allow={canReadProviderRoster}>
                <ContratistasAdmin />
              </RequireRoleCapability>
            </RequireAdminModule>
          }
        />
        <Route
          path="activos"
          element={
            <RequireAdminModule section="activos" fallback="/tiendas">
              <RequireRoleCapability allow={canAdministerOrg}>
                <Activos />
              </RequireRoleCapability>
            </RequireAdminModule>
          }
        />
        <Route
          path="checklists"
          element={
            <RequireAdminModule section="checklists" fallback={ADMIN_BASE_PATH}>
              <RequireRoleCapability allow={canUseChecklistsSection}>
                <ChecklistsAdmin />
              </RequireRoleCapability>
            </RequireAdminModule>
          }
        />
        <Route
          path="mantenciones"
          element={
            <RequireAdminModule section="mantenciones" fallback="/">
              <RequireRoleCapability allow={canManageMaintenanceItems}>
                <Mantenciones />
              </RequireRoleCapability>
            </RequireAdminModule>
          }
        />
        <Route
          path="enlaces"
          element={
            <RequireAdminModule section="enlaces" fallback="/enlaces">
              <Enlaces />
            </RequireAdminModule>
          }
        />
        <Route
          path="registro"
          element={
            <RequireAdminModule section="registro" fallback="/">
              <RequireRoleCapability allow={canAdministerOrg}>
                <Registro />
              </RequireRoleCapability>
            </RequireAdminModule>
          }
        />
      </Route>
    </Route>
  );
}

export function App() {
  // Mounted once here, not duplicated into Shell.tsx/AdminShell.tsx
  // (BIT-44). App is the single ancestor of both route trees — Shell and
  // AdminShell are siblings, never simultaneously mounted, so a
  // per-shell instance would have "worked" today, but only by accident:
  // two independent writers to one global attribute with no arbitration
  // is one nested-route refactor away from a stale reading. One writer
  // here removes the question entirely, and covers /login and
  // /auth/callback too even though neither needs it — a no-op for those
  // two, not a liability.
  useAppReadySignal();
  usePostHogPageview();

  return (
    <>
      <SessionExpiryHandler />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* /dev/** — navigable prototype (BIT-74, ADR-0076), structurally
            absent from the emitted bundle unless DEV_ROUTES_ENABLED is true
            at build time (see the DevRoutes const above). Not wrapped in
            RequireAuth: the exclusion is "does not exist in this artifact",
            not "requires a session". */}
        {DevRoutes && (
          <Route
            path="/dev/*"
            element={
              <Suspense fallback={null}>
                <DevRoutes />
              </Suspense>
            }
          />
        )}
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <RequirePlatformAdmin>
                <AdminShell />
              </RequirePlatformAdmin>
            </RequireAuth>
          }
        >
          <Route index element={<AdminOverview />} />
          <Route path="orgs" element={<AdminOrgs />} />
          <Route path="orgs/:id" element={<AdminOrgDetail />} />
          <Route path="orgs/:id/reports" element={<AdminOrgReports />} />
          <Route path="carga-masiva" element={<AdminCargaMasiva />} />
          <Route path="*" element={<AdminOverview />} />
        </Route>
        {/* /reportes/:id — the closed-report reading view (design handoff
            reporte_escritorio §2). A sibling of the Shell group, not a
            child: it opens in its own tab from DetalleTienda's timeline
            and renders its own sticky .rp-top chrome with no sidebar.
            Same auth + role gate as every Shell route. */}
        <Route
          path="reportes/:id"
          element={
            <RequireAuth>
              <RequireDashboardRole>
                <Reporte />
              </RequireDashboardRole>
            </RequireAuth>
          }
        />
        <Route
          element={
            <RequireAuth>
              <RequireDashboardRole>
                <Shell />
              </RequireDashboardRole>
            </RequireAuth>
          }
        >
          <Route index element={<OverviewRoute />} />
          {dashboardSurfaceRoutes()}
          <Route path="*" element={<OverviewRoute />} />
        </Route>
      </Routes>
    </>
  );
}
